import { FacebookIntegration } from 'api/clients/ExternalIntegrationsClient'
import Styles from './FacebookPreview.module.scss'
import { Earth } from './icons'
import { ReactNode, RefObject, useEffect, useMemo, useState } from 'react'
import { MoreButton } from '../components/MoreButton/MoreButton'
import { useTruncateText } from '../hooks/useTruncateText'
import { getIntegrationDetails } from '../getIntegrationDetails'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { PollingPreview } from '../components/PollingPreview/PollingPreview'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { FacebookPhotoBox } from './FacebookPhotoBox'
import { highlightContent } from '../highlightContent'
import { ConnectButton } from 'components/ArticlePostPreview/ConnectButton/ConnectButton'
import { MediaObject } from 'types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import cx from 'classnames'
import * as CSS from 'csstype'
import { collapseMultipleNewLines } from 'utils/string'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditCaptionOverlay } from '../components/EditCaptionOverlay/EditCaptionOverlay'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'

interface FacebookPreviewProps {
  integration?: FacebookIntegration
  content: string
  mediaObjects?: MediaObject[]
  loading?: boolean
  pollContentStream?: boolean
  hideConnectButton?: boolean
  onEdit?: () => void
  containerClassName?: string
  photoBoxClassName?: string
  customPreviewContent?: ReactNode
  maxWidth?: number | null
  showVideoControls?: boolean
  isActive?: boolean
  isInsertingMedia?: boolean
  isTransitioning?: boolean
  showHoverActionOverlay?: boolean
  isGenerating?: boolean
  almanacFileKey?: string
  scheduledPost?: ScheduledPostShow
  onClose?: () => void
  alwaysTruncateCaption?: boolean
  postPreviewHeaderRef?: RefObject<HTMLDivElement>
  postPreviewContentRef?: RefObject<HTMLDivElement>
  postPreviewFooterRef?: RefObject<HTMLDivElement>
  onSeeMoreToggle?: (expanded: boolean) => void
  activePlatform?: ExternalIntegrationEnum
}

export const FacebookPreview = ({
  mediaObjects,
  content,
  integration,
  loading,
  pollContentStream = true,
  hideConnectButton,
  onEdit,
  containerClassName,
  photoBoxClassName,
  customPreviewContent,
  showVideoControls,
  maxWidth = 550,
  isActive = true,
  isInsertingMedia = false,
  isTransitioning = false,
  showHoverActionOverlay = false,
  isGenerating = false,
  almanacFileKey,
  scheduledPost,
  onClose,
  alwaysTruncateCaption = false,
  postPreviewHeaderRef,
  postPreviewContentRef,
  onSeeMoreToggle,
  activePlatform,
}: FacebookPreviewProps) => {
  const { name, picture } = getIntegrationDetails(integration)
  const { isMobile, isMobileSm } = useResponsiveDesign()
  const isPostPreviewV2 = useFlipperFlag('post_preview_v2')

  const formattedContent = useMemo<string>(() => collapseMultipleNewLines(content) ?? '', [content])

  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content: formattedContent,
    truncatedByDefault:
      alwaysTruncateCaption || ((!isMobile || isPostPreviewV2) && (!!mediaObjects?.length || !!customPreviewContent)),
  })
  const [imagesLoading, setImagesLoading] = useState(false)

  useEffect(() => {
    if (activePlatform && activePlatform !== ExternalIntegrationEnum.Facebook && !isTruncated) {
      setIsTruncated(true)
    }
  }, [activePlatform, isTruncated, setIsTruncated])

  const highlightClassNames = {
    linkClassName: Styles.highlight,
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const userName = isMobileSm && name === 'Account Not Connected' ? 'Not Connected' : name

  const style: CSS.Properties = {
    ['--max-width' as any]: `${maxWidth}px`,
  }

  return (
    <div className={cx(Styles.container, containerClassName)} data-testid="fb-preview" style={style}>
      <ConditionalWrapper
        condition={showHoverActionOverlay && almanacFileKey}
        wrapper={(children) => {
          if (!scheduledPost || !almanacFileKey) return children

          return (
            <EditCaptionOverlay
              scheduledPost={scheduledPost}
              almanacFileKey={almanacFileKey}
              borderRadius={0}
              position="top-right"
              onClose={onClose}
            >
              {children}
            </EditCaptionOverlay>
          )
        }}
      >
        <div className={Styles.header} ref={postPreviewHeaderRef}>
          <img src={picture} alt={`${name || 'User'}'s profile`} className={Styles.profilePicture} />
          <div className={Styles.details}>
            <div className={Styles.primaryDetails}>
              <span className={Styles.profileName}>{userName}</span>

              {!hideConnectButton && isMobile && <ConnectButton margin="-10px -10px 0 auto" />}

              {onEdit && (
                <div className={Styles.editButton}>
                  <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                    Edit
                  </Button>
                </div>
              )}
            </div>

            <div className={Styles.secondaryDetails}>
              <span className={Styles.timestamp}>3 days ago</span>
              <span> · </span>
              <span>
                <Earth />
              </span>
            </div>
          </div>
        </div>
        <div className={Styles.content} ref={postPreviewContentRef}>
          {(() => {
            if (isGenerating) return <CaptionSkeleton />
            if (loading && pollContentStream) {
              return (
                <PollingPreview
                  setImagesLoading={setImagesLoading}
                  imagesLoading={imagesLoading}
                  channel={ChannelType.FacebookPost}
                />
              )
            }
            return (
              <>
                <span
                  ref={textRef as React.RefObject<HTMLSpanElement>}
                  key={`caption-${formattedContent}`}
                  className={cx({ [Styles.truncated]: isTruncated })}
                  dangerouslySetInnerHTML={{
                    __html: highlightContent(formattedContent, highlightClassNames),
                  }}
                />
                {shouldShowMoreButton && !alwaysTruncateCaption && (
                  <MoreButton
                    isTruncated={isTruncated}
                    setIsTruncated={() => {
                      setIsTruncated(!isTruncated)
                      onSeeMoreToggle?.(isTruncated)
                    }}
                  />
                )}
              </>
            )
          })()}
        </div>
      </ConditionalWrapper>
      {customPreviewContent || (
        <FacebookPhotoBox
          mediaObjects={mediaObjects}
          imagesLoading={loading && imagesLoading}
          className={photoBoxClassName}
          showVideoControls={showVideoControls}
          isActive={isActive}
          showHoverActionOverlay={showHoverActionOverlay}
          almanacFileKey={almanacFileKey}
          isInsertingMedia={isInsertingMedia}
          isPressable={!showHoverActionOverlay}
          isGenerating={isGenerating}
          onClose={onClose}
        />
      )}
    </div>
  )
}
